/**
 * Keep or remove the lines of a text that match some terms.
 *
 * Each non-empty line of the term box is one term: a literal string, matched with
 * optional whole-word, start-of-line, end-of-line or whole-line anchoring, or a regular
 * expression. Several terms combine as "any" (a line matches if one does) or "all".
 * Everything is a JavaScript `RegExp` underneath, built with the `u` flag so letters
 * outside ASCII count as letters and `i` folds case the Unicode way.
 */

export type MatchKind = "text" | "regex";
export type MatchPosition = "contains" | "starts" | "ends" | "exact";
export type Combine = "any" | "all";
export type FilterAction = "keep" | "remove";

export interface FilterOptions {
  action: FilterAction;
  kind: MatchKind;
  position: MatchPosition;
  combine: Combine;
  caseSensitive: boolean;
  /** Text terms only: the match may not touch a letter, digit or underscore. */
  wholeWord: boolean;
  /** Ignore spaces and tabs at both ends of a line when matching (output keeps them). */
  trim: boolean;
  /** Keep empty lines whatever the filter would do with them. */
  keepBlank: boolean;
}

export const DEFAULT_FILTER_OPTIONS: FilterOptions = {
  action: "keep",
  kind: "text",
  position: "contains",
  combine: "any",
  caseSensitive: false,
  wholeWord: false,
  trim: true,
  keepBlank: false,
};

export type TermProblem = "invalid" | "slow";

export interface TermError {
  /** 1-based position among the non-empty term lines. */
  term: number;
  source: string;
  problem: TermProblem;
  /** The engine's explanation for an invalid expression. */
  message: string;
}

export interface TermStat {
  source: string;
  /** Lines this term matched. */
  lines: number;
  /** Matches of this term, counting several in one line. */
  matches: number;
}

export interface MatchedLine {
  /** 1-based line number in the input. */
  line: number;
  text: string;
  matches: number;
}

export interface FilterResult {
  errors: TermError[];
  output: string;
  linesIn: number;
  linesMatched: number;
  linesKept: number;
  linesRemoved: number;
  /** Matches of all terms together, every occurrence counted. */
  totalMatches: number;
  terms: TermStat[];
  matched: MatchedLine[];
}

const RE_LINE_BREAK = /\r\n|\r|\n/;
const RE_TRAILING_BREAK = /(?:\r\n|\r|\n)$/;
const RE_CRLF = /\r\n/;
const RE_ESCAPE = /[.*+?^${}()|[\]\\]/g;
const RE_TRIM = /^[ \t ]+|[ \t ]+$/g;
const RE_MESSAGE_PREFIX =
  /^Invalid regular expression:\s*(?:\/.*\/[a-z]*:\s*)?/;
/** A group that ends in a quantifier and is itself quantified: `(a+)+`, `(.*)*`. */
const RE_NESTED_QUANTIFIER = /\((?:[^()\\]|\\.)*[+*}]\)[+*{]/;
const WORD_START = "(?<![\\p{L}\\p{N}_])";
const WORD_END = "(?![\\p{L}\\p{N}_])";

export function escapeRegExp(text: string): string {
  return text.replace(RE_ESCAPE, "\\$&");
}

/** Split into lines; a final line break does not make an extra empty line. */
export function splitLines(input: string): {
  lines: string[];
  crlf: boolean;
  trailing: boolean;
} {
  const trailing = RE_TRAILING_BREAK.test(input);
  const body = trailing ? input.replace(RE_TRAILING_BREAK, "") : input;
  return {
    lines: input === "" ? [] : body.split(RE_LINE_BREAK),
    crlf: RE_CRLF.test(input),
    trailing,
  };
}

export function termSources(raw: string): string[] {
  return raw
    .split(RE_LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

function edge(
  anchored: boolean,
  anchor: string,
  wholeWord: boolean,
  boundary: string
) {
  if (anchored) {
    return anchor;
  }
  return wholeWord ? boundary : "";
}

function textPattern(source: string, options: FilterOptions): string {
  const startsLine =
    options.position === "starts" || options.position === "exact";
  const endsLine = options.position === "ends" || options.position === "exact";
  const left = edge(startsLine, "^", options.wholeWord, WORD_START);
  const right = edge(endsLine, "$", options.wholeWord, WORD_END);
  return `${left}${escapeRegExp(source)}${right}`;
}

function cleanMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return message.replace(RE_MESSAGE_PREFIX, "");
}

/** Build a global regular expression, or say why it cannot be built. */
function compile(
  source: string,
  options: FilterOptions
): { regex: RegExp } | { problem: TermProblem; message: string } {
  if (options.kind === "regex" && RE_NESTED_QUANTIFIER.test(source)) {
    return { problem: "slow", message: "" };
  }
  const pattern =
    options.kind === "regex" ? source : textPattern(source, options);
  const flags = options.caseSensitive ? "g" : "gi";
  try {
    return { regex: new RegExp(pattern, `${flags}u`) };
  } catch (strict) {
    // Some everyday patterns (`\-`, `\_`) are errors only under the `u` flag.
    if (options.kind === "regex") {
      try {
        return { regex: new RegExp(pattern, flags) };
      } catch {
        // Report the first message below.
      }
    }
    return { problem: "invalid", message: cleanMessage(strict) };
  }
}

function countMatches(regex: RegExp, line: string): number {
  regex.lastIndex = 0;
  return [...line.matchAll(regex)].length;
}

interface Compiled {
  source: string;
  regex: RegExp;
}

function compileAll(
  sources: readonly string[],
  options: FilterOptions
): { compiled: Compiled[]; errors: TermError[] } {
  const compiled: Compiled[] = [];
  const errors: TermError[] = [];
  sources.forEach((source, index) => {
    const built = compile(source, options);
    if ("regex" in built) {
      compiled.push({ source, regex: built.regex });
    } else {
      errors.push({
        term: index + 1,
        source,
        problem: built.problem,
        message: built.message,
      });
    }
  });
  return { compiled, errors };
}

const MAX_MATCHED_LINES = 500;

/** Test one line against every term, adding to each term's tally. */
function scoreLine(
  compiled: readonly Compiled[],
  stats: readonly TermStat[],
  subject: string,
  combine: Combine
): { isMatch: boolean; hits: number } {
  let hits = 0;
  let termsHit = 0;
  compiled.forEach((term, termIndex) => {
    const count = countMatches(term.regex, subject);
    const stat = stats[termIndex];
    if (count > 0 && stat) {
      stat.lines += 1;
      stat.matches += count;
      termsHit += 1;
      hits += count;
    }
  });
  const isMatch =
    combine === "all" ? termsHit === compiled.length : termsHit > 0;
  return { isMatch, hits };
}

export function filterLines(
  input: string,
  termBox: string,
  options: FilterOptions
): FilterResult {
  const { lines, crlf, trailing } = splitLines(input);
  const { compiled, errors } = compileAll(termSources(termBox), options);
  if (errors.length > 0 || compiled.length === 0) {
    return {
      errors,
      output: input,
      linesIn: lines.length,
      linesMatched: 0,
      linesKept: lines.length,
      linesRemoved: 0,
      totalMatches: 0,
      terms: [],
      matched: [],
    };
  }
  const stats: TermStat[] = compiled.map((term) => ({
    source: term.source,
    lines: 0,
    matches: 0,
  }));
  const matched: MatchedLine[] = [];
  const kept: string[] = [];
  let linesMatched = 0;
  let totalMatches = 0;
  lines.forEach((line, index) => {
    const subject = options.trim ? line.replace(RE_TRIM, "") : line;
    const { isMatch, hits } = scoreLine(
      compiled,
      stats,
      subject,
      options.combine
    );
    if (isMatch) {
      linesMatched += 1;
      totalMatches += hits;
      if (matched.length < MAX_MATCHED_LINES) {
        matched.push({ line: index + 1, text: line, matches: hits });
      }
    }
    const blankKept = options.keepBlank && line.trim() === "";
    if (blankKept || isMatch === (options.action === "keep")) {
      kept.push(line);
    }
  });
  const separator = crlf ? "\r\n" : "\n";
  const body = kept.join(separator);
  return {
    errors,
    output: trailing && kept.length > 0 ? `${body}${separator}` : body,
    linesIn: lines.length,
    linesMatched,
    linesKept: kept.length,
    linesRemoved: lines.length - kept.length,
    totalMatches,
    terms: stats,
    matched,
  };
}
